import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useValidation } from 'hooks'
import { makeStyles } from '@material-ui/core/styles'
import style from './style'
import Icon from '../Icon/Icon'
import Typography from '../Typography/Typography'
import IconButton from '../IconButton/IconButton'
import CommonInputType from '../../helpers/types/CommonInput.interface'
import RequiredAsterisk from '../RequiredAsterisk/RequiredAsterisk'

const useStyles = makeStyles(style)

const getHelperText = (hText) => {
    if (hText && typeof hText === 'string') {
        return <>
            <Icon color='neutral-400' name='notification-alert' size={16} className='helper-text-icon' />
            <Typography tag='span' variant='body4' color='neutral-400' className='helper-text'>{hText}</Typography>
        </>
    }

    return hText
}

const renderHelperText = (hText, stls) => {
    if (!hText) return ''
    return <div className={stls.inputHelperText} data-testid='helper-text'>
        {getHelperText(hText)}
    </div>
}

interface PropsInterface extends CommonInputType {
    children: string | JSX.Element | JSX.Element[];
    label?: string | false
    onClickWrapper?: React.MouseEventHandler<HTMLDivElement>
}

/**
 *
 */
const CommonInput = ({
    value,
    rules,
    isValid,
    valueHasBeenChanged,
    valueHasBeenChangedAtLeastOnce,
    validateOn = 'change',
    errorsToShow = 1,
    children,
    helperText,
    onClear,
    style,
    disabled,
    clearable = false,
    size,
    fullWidth = false,
    autoWidth = false,
    className,
    label = false,
    labelPlacement = 'floating',
    leadingIcon,
    trailingIcon,
    maxLength,
    onClickLeadingIcon,
    onClickTrailingIcon,
    onClickWrapper,
    id,
    suppressAsterisk = false,
    'data-testid': dataTestID = ''
}: PropsInterface): JSX.Element => {
    const styles = useStyles({
        disabled,
        maxLength,
        hasLeadingIconAction: !!onClickLeadingIcon,
        hasTrailingIconAction: !!onClickTrailingIcon
    })

    const originalValue = useRef<any>(null)
    const inputWrapperRef = useRef<HTMLDivElement>(null)
    const hasInsideLabel = !!label && labelPlacement === 'inside'
    const updateInputWidth = () => {
        const wrapper = inputWrapperRef.current
        if (!wrapper) return
        wrapper.style.removeProperty('--input-value-min-width')
        if (!autoWidth) return
        const input = wrapper.querySelector<HTMLInputElement | HTMLTextAreaElement>(
            ':scope > input, :scope > textarea, :scope > .select > input'
        )
        if (!input) return
        const computed = window.getComputedStyle(input)
        const sizer = document.createElement('span')
        Object.assign(sizer.style, {
            position: 'absolute',
            visibility: 'hidden',
            whiteSpace: 'pre',
            fontFamily: computed.fontFamily,
            fontSize: computed.fontSize,
            fontWeight: computed.fontWeight,
            fontStyle: computed.fontStyle,
            letterSpacing: computed.letterSpacing
        })
        sizer.textContent = input.value || input.placeholder
        wrapper.appendChild(sizer)
        const padding = (parseFloat(computed.paddingLeft) || 0) + (parseFloat(computed.paddingRight) || 0)
        const border = (parseFloat(computed.borderLeftWidth) || 0) + (parseFloat(computed.borderRightWidth) || 0)
        const width = Math.ceil(sizer.getBoundingClientRect().width + padding + border) + 2
        sizer.remove()
        wrapper.style.setProperty('--input-value-min-width', `${width}px`)
    }
    useLayoutEffect(() => {
        updateInputWidth()
        // const fonts = (document as Document & { fonts?: EventTarget }).fonts
        // if (!autoWidth || !fonts) return
        // fonts.addEventListener('loadingdone', updateInputWidth)
        // return () => fonts.removeEventListener('loadingdone', updateInputWidth)
    })
    const hasOriginalValue = useRef(false)
    const [hasValueBeenChanged, setHasValueBeenChanged] = useState<boolean>(false)
    const [inputState, setInputState] = useState<'focus' | 'blur' | 'unknown'>('unknown')

    const [errors] = useValidation(
        value,
        rules,
        isValid,
        validateOn,
        hasValueBeenChanged,
        inputState
    )

    const dataTestId = `${dataTestID}-common-input`
    const wrapperClasses = [
        className,
        'input-wrapper',
        errors && errors.length ? 'has-error' : '',
        leadingIcon && 'has-leading-icon',
        trailingIcon && 'has-trailing-icon',
        hasInsideLabel && 'has-inside-label',
        autoWidth && 'has-auto-width',
        size || ''
    ].filter((x) => x).join(' ')

    useEffect(() => {
        if (!hasOriginalValue.current) {
            originalValue.current = value
            hasOriginalValue.current = true
        } else {
            setHasValueBeenChanged(true)
            valueHasBeenChanged && valueHasBeenChanged(originalValue.current !== value)
        }
    }, [value])

    useEffect(() => {
        if (hasOriginalValue.current) valueHasBeenChangedAtLeastOnce && valueHasBeenChangedAtLeastOnce(hasValueBeenChanged)
    }, [hasValueBeenChanged])

    const userMayClearTheField = value && !disabled && clearable && onClear
    const isAsteriskRequired = !suppressAsterisk && rules?.some((x) => x.name === 'validateRequired')
    const fieldLabel = label && <label
        className={hasInsideLabel ? 'label-inside' : (labelPlacement === 'floating' ? 'label-in-border' : 'label-above')}
        htmlFor={id}
    >
        {label}{isAsteriskRequired ? <RequiredAsterisk /> : ''}{hasInsideLabel ? ':' : ''}
    </label>

    return (
        <div
            className={`
                foundations-common-input
                ${autoWidth ? 'common-input--auto-width' : ''}
                ${value ? 'foundations-common-input--has-value' : ''}
                ${styles.commonInput}
                ${fullWidth && 'common-input--full-width'}
                ${userMayClearTheField && 'common-input-clearable'}
                ${className || ''}`
            }
            data-testid={dataTestId}
        >
            {labelPlacement === 'above' && fieldLabel}
            <div
                ref={inputWrapperRef}
                className={wrapperClasses}
                style={style}
                onFocus={() => setInputState('focus')}
                onBlur={() => setInputState('blur')}
                onClick={onClickWrapper}
                onInputCapture={autoWidth ? updateInputWidth : undefined}
            >
                {labelPlacement !== 'above' && fieldLabel}
                {
                    leadingIcon &&
                    <Icon
                        onMouseDown={() => !disabled && onClickLeadingIcon && onClickLeadingIcon()}
                        color={disabled ? 'neutral-300' : 'neutral-600'}
                        name={leadingIcon}
                        size={20}
                        className='leading-icon'
                        data-testid={`${dataTestID}-leading-icon`}
                    />
                }
                {children}
                {userMayClearTheField && <IconButton
                    className='clear-input-button'
                    size='small'
                    variant='icon'
                    color='neutral'
                    name='close'
                    onMouseDown={onClear}
                    type='button'
                    data-testid='clear-field-button'
                />}
                {
                    trailingIcon &&
                    <Icon
                        name={trailingIcon}
                        onMouseDown={() => !disabled && onClickTrailingIcon && onClickTrailingIcon()}
                        color={disabled ? 'neutral-300' : 'neutral-600'}
                        size={20}
                        className='trailing-icon'
                        data-testid={`${dataTestID}-trailing-icon`}
                    />
                }
            </div>

            {errors?.length
                ? (<Typography variant='helperText' color='destructive-500' data-testid='validation-errors'>
                    {errors.slice(0, errorsToShow).map((x, i) => <div className={styles.validationError} key={`validation-error-${i}`}>{x}</div>)}
                </Typography>)
                : <></>}

            {renderHelperText(helperText, styles)}
        </div>
    )
}

export default CommonInput
